
import { api } from "@/api/api";
import { ApiGetCustomerDto, ApiUpsertCourseParticipantDto, ApiRole, ApiGetCostTypeDto } from "@/api/generated/Api";
import BaseModalForm from "@/components/shared/modal/BaseModalForm.vue";
import { NotificationItemType } from "@/shared/enums/notificationItemEnum";
import { globalLoadingWrapper } from "@/shared/helpers/loadingHelpers";
import { openNotification } from "@/shared/helpers/store.helpers";
import { validateNotEmpty } from "@/shared/helpers/validationHelpers";
import { useRoute, useStore } from "@/shared/useHelpers";
import { StoreState } from "@/store/store.state.interface";
import { computed, defineComponent, onMounted, ref } from "@vue/composition-api";
import { getValidatableRef } from "@/shared/helpers/typeHelpers";
import { localeSortByProperty } from "@/shared/helpers/arrayHelpers";
import { formatDate } from "@/shared/helpers/dateHelpers";
import EmployeeSearchInput from "@/components/common/EmployeeSearchInput.vue";
import PersonSearchInput from "@/components/common/PersonSearchInput.vue";

export default defineComponent({
  name: "ResourceModal",
  components: { BaseModalForm, EmployeeSearchInput, PersonSearchInput },
  props: {
    type: {
      type: String,
      required: true,
    },
    headline: {
      type: String,
      required: true,
    },
    isVocationalSchool: {
      type: Boolean,
      required: true,
    },
  },
  setup(props, context) {
    const store = useStore<StoreState>();
    const route = useRoute();
    const aofEmployee = ref(false);
    const getEmployees = ref([]);
    const employeeNumber = ref();
    const addressBookList = ref<ApiGetCustomerDto[]>([]);
    const resourceRoleList = ref<ApiRole[]>([]);
    const costTypeList = ref<ApiGetCostTypeDto[]>([]);

    const hours = ref<number | undefined>();
    const costTypeId = ref<number>(510);

    const resourceData = ref<ApiUpsertCourseParticipantDto>({
      userId: 0,
      roleName: "",
      status: "påmeldt",
      requiresElectronicInvoice: false,
    });

    onMounted(async () => {
      globalLoadingWrapper({ blocking: true }, async () => {
        await loadCostTypes();
        await loadResourceRoleList();
      });
    });

    const postResource = async () => {
      globalLoadingWrapper({ blocking: true }, async () => {
        await api.course.upsertCourseParticipantAsync(+route.params.id, resourceData.value);
        if (aofEmployee.value && hours.value && costTypeId.value) {
          await api.course.insertEmployeeContractLine(+route.params.id, resourceData.value.userId, {
            amount: hours.value,
            costTypeId: costTypeId.value,
          });
        }
        openNotification(store, NotificationItemType.Success, "Ressursen er lagt til kurset");
        context.emit("close");
      });
    };

    const loadCostTypes = async () => {
      const response = await api.economy.getCostTypesAsync();
      costTypeList.value = response.data;
    };

    const loadResourceRoleList = async () => {
      resourceRoleList.value = (await api.role.getRolesAsync()).data
        .filter((role) => role.isActive && role.isResource && role.isForVocationalSchool === props.isVocationalSchool)
        .sort(localeSortByProperty("name"));
    };

    const onSubmit = async () => {
      const isValid = getValidatableRef(context.refs.form)?.validate();
      if (isValid) {
        postResource();
      }
    };

    return {
      employeeNumber,
      resourceData,
      hours,
      aofEmployee,
      getEmployees,
      resourceRoleList,
      addressBookList,
      costTypeId,
      costTypeList,
      formatDate,
      onSubmit,
      validateNotEmpty,
      contactsList: computed(() => store.state.contacts.persons),
      updateUserId: (userId: number) => (resourceData.value.userId = userId),
    };
  },
});
